// src/components/common/NotProvided.tsx

import React, { CSSProperties } from 'react';
import { useTheme } from '@mui/material';

type TypeConfig = {
  border?: string;
  backgroundColor?: string;
  padding?: string;
  fontSize?: string;
  color?: string;
  fontWeight?: string | number;
  fontStyle?: CSSProperties['fontStyle'];
  textAlign?: CSSProperties['textAlign'];
  width?: string;
  defaultText?: string;
};

const minimalDefaults: TypeConfig = {
  border: undefined,
  backgroundColor: undefined,
  padding: undefined,
  fontSize: 'inherit',
  color: 'inherit',
  fontWeight: 'normal',
  fontStyle: undefined,
  textAlign: 'left',
  width: undefined,
  defaultText: '',
};

const getTypeConfigurations = (theme: any): Record<string, TypeConfig> => ({
  NotProvided: {
    padding: '0',
    fontSize: 'inherit',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.main,
    fontWeight: 500,
    fontStyle: 'inherit',
    textAlign: 'left',
    width: undefined,
    defaultText: 'none',
  },

  EmptyState: {
    border: 'none',
    backgroundColor: '#fafafa',
    fontSize: 'inherit',
    color: 'inherit',
    fontWeight: 400,
    fontStyle: 'italic',
    textAlign: 'left',
    width: '100%',
    defaultText: 'No items found',
  },

  Error: {
    border: 'none',
    backgroundColor: '#ffe0e0',
    padding: '1rem',
    fontSize: 'inherit',
    color: '#d32f2f',
    fontWeight: 600,
    fontStyle: undefined,
    textAlign: 'left',
    width: undefined,
    defaultText: 'Error loading data',
  },

  Warning: {
    border: 'none',
    backgroundColor: '#fff3cd',
    padding: '1rem',
    fontSize: 'inherit',
    color: '#ff9800',
    fontWeight: 500,
    fontStyle: undefined,
    textAlign: 'left',
    width: undefined,
    defaultText: 'Warning',
  },
});

const getJustifyContent = (
  textAlign?: CSSProperties['textAlign']
): CSSProperties['justifyContent'] => {
  switch (textAlign) {
    case 'right':
    case 'end':
      return 'flex-end';
    case 'center':
      return 'center';
    case 'left':
    case 'start':
    default:
      return 'flex-start';
  }
};

interface NotProvidedProps {
  children?: React.ReactNode;
  color?: string;
  fontWeight?: number | string;
  padding?: string;
  fontSize?: string;
  border?: string;
  backgroundColor?: string;
  width?: string;
  textAlign?: CSSProperties['textAlign'];
  fontStyle?: CSSProperties['fontStyle'];
  type?: keyof ReturnType<typeof getTypeConfigurations>;
}

export const NotProvided: React.FC<NotProvidedProps> = ({
  children,
  color,
  fontWeight,
  padding,
  fontSize,
  border,
  backgroundColor,
  width,
  textAlign,
  fontStyle,
  type,
}) => {
  const theme = useTheme();
  const typeConfigurations = getTypeConfigurations(theme);

  let config: TypeConfig = minimalDefaults;

  if (type) {
    const typeConfig = typeConfigurations[type];
    if (typeConfig) {
      config = typeConfig;
    }
  }

  const finalColor = color ?? config.color;
  const finalFontWeight = fontWeight ?? config.fontWeight;
  const finalPadding = padding ?? config.padding;
  const finalFontSize = fontSize ?? config.fontSize;
  const finalBorder = border ?? config.border;
  const finalBackgroundColor = backgroundColor ?? config.backgroundColor;
  const finalWidth = width ?? config.width;
  const finalTextAlign = textAlign ?? config.textAlign;
  const finalFontStyle = fontStyle ?? config.fontStyle;
  const finalJustifyContent = getJustifyContent(finalTextAlign);

  const styles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: finalJustifyContent,
    minHeight: '3rem',
  };

  if (finalBorder !== undefined) styles.border = finalBorder;
  if (finalBackgroundColor !== undefined)
    styles.backgroundColor = finalBackgroundColor;
  if (finalPadding !== undefined) styles.padding = finalPadding;
  if (finalFontSize !== undefined) styles.fontSize = finalFontSize;
  if (finalColor !== undefined) styles.color = finalColor;
  if (finalFontWeight !== undefined) styles.fontWeight = finalFontWeight;
  if (finalFontStyle !== undefined) styles.fontStyle = finalFontStyle;
  if (finalWidth !== undefined) styles.width = finalWidth;
  if (finalTextAlign !== undefined) styles.textAlign = finalTextAlign;

  return <div style={styles}>{children ?? config.defaultText}</div>;
};

export default NotProvided;
